/** Detay ekranı: Sinematik backdrop, teknik rozetler ve sezon/bölüm gezgini. */

import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import {
  ApiError,
  api,
  type Item,
  type ItemCollection,
  type ItemDetail,
  type ItemMetadata,
  type Library,
  type MetadataOverrideInput,
} from '../api.ts';
import { ItemCard } from '../components/ItemCard.tsx';
import { useItemDelete } from '../components/ItemDelete.tsx';
import { MetadataMatchDialog } from '../components/MetadataMatchDialog.tsx';
import { MetadataPanel } from '../components/MetadataPanel.tsx';
import { Empty, ErrorNotice, Loading } from '../components/States.tsx';
import { fileToBase64 } from '../fileBase64.ts';
import { displayTitle, formatDuration, progressRatio } from '../format.ts';
import {
  BookmarkFilledIcon,
  BookmarkIcon,
  CaptionsIcon,
  FilmIcon,
  PlayIcon,
  TrashIcon,
  TvIcon,
} from '../Icon.tsx';
import { resizeProxyImage } from '../imageSize.ts';
import { type MediaCopy, mediaContextFor, mediaCopy } from '../mediaCopy.ts';
import { mediaUrl } from '../mediaToken.ts';
import { overviewPreview, preferredRating } from '../metadataView.ts';
import { loadOfflineItem, saveOfflineItem } from '../offlineCatalog.ts';
import { useSession } from '../session.tsx';

const KIND_LABEL: Record<string, string> = {
  movie: 'Film',
  show: 'Dizi',
  season: 'Sezon',
  episode: 'Bölüm',
};

/** Ağ/5xx hatasında son görüntülenen detayı döndürür; 404'te önbelleğe düşmez. */
async function cachedItemAfterError(
  userId: string,
  itemId: string,
  caught: unknown,
): Promise<ItemDetail | null> {
  if (caught instanceof ApiError && caught.status === 404) return null;
  return loadOfflineItem(userId, itemId);
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: detay ekranı metadata, koleksiyon ve izleme durumunu tek yerde yönetir
export function ItemScreen({
  itemId,
  libraries,
  onWatch,
  onBack,
}: {
  itemId: string;
  /** Müzik kütüphanesindeki uzantısız parçaları doğru sınıflamak için. */
  libraries?: Library[] | null;
  /** `startOver` yalnız "Baştan başlat" için; normal oynatma kayıtlı konumu kullanır. */
  onWatch: (itemId: string, startOver?: boolean) => void;
  onBack?: (() => void) | undefined;
}): ReactNode {
  const [detail, setDetail] = useState<ItemDetail | null>(null);
  const [nextTarget, setNextTarget] = useState<Item | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [watchlistBusy, setWatchlistBusy] = useState(false);
  const { user } = useSession();
  const isAdmin = user?.role === 'admin';
  const { canDelete, requestDelete } = useItemDelete();
  const [metadata, setMetadata] = useState<ItemMetadata | null>(null);
  const [metadataEnabled, setMetadataEnabled] = useState(false);
  const [metadataBusy, setMetadataBusy] = useState(false);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [showMatch, setShowMatch] = useState(false);
  const [collection, setCollection] = useState<ItemCollection | null>(null);
  const [watchBusy, setWatchBusy] = useState(false);

  useEffect(() => {
    setDetail(null);
    setError(null);
    let cancelled = false;
    void api
      .item(itemId)
      .then((found) => {
        if (!cancelled) {
          setDetail(found);
          setInWatchlist(found.inWatchlist ?? false);
          if (user !== null) void saveOfflineItem(user.id, found);
        }
      })
      .catch(async (caught: unknown) => {
        if (cancelled) return;
        const cached = user === null ? null : await cachedItemAfterError(user.id, itemId, caught);
        if (!cancelled && cached !== null) {
          setDetail(cached);
          setInWatchlist(cached.inWatchlist ?? false);
          return;
        }
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : 'Öğe okunamadı.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [itemId, user]);

  // Dizi/sezon detayında doğru "devam et" hedefi: ilk izlenmemiş bölüm.
  useEffect(() => {
    if (detail === null || (detail.kind !== 'show' && detail.kind !== 'season')) {
      setNextTarget(null);
      return;
    }
    let cancelled = false;
    void api
      .nextItem(detail.id)
      .then((result) => {
        if (!cancelled) setNextTarget(result.item);
      })
      .catch(() => {
        if (!cancelled) setNextTarget(null);
      });
    return () => {
      cancelled = true;
    };
  }, [detail]);

  useEffect(() => {
    setCollection(null);
    if (detail?.kind !== 'movie') return;
    let cancelled = false;
    void api
      .itemCollection(detail.id)
      .then((result) => {
        if (!cancelled) setCollection(result);
      })
      .catch(() => {
        if (!cancelled) setCollection(null);
      });
    return () => {
      cancelled = true;
    };
  }, [detail?.id, detail?.kind]);

  useEffect(() => {
    let cancelled = false;
    setMetadata(null);
    setMetadataError(null);
    void api
      .itemMetadata(itemId)
      .then((result) => {
        if (!cancelled) {
          setMetadata(result.metadata);
          setMetadataEnabled(result.enabled);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMetadata(null);
          setMetadataEnabled(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  const refreshMetadata = async (): Promise<void> => {
    if (metadataBusy) return;
    setMetadataBusy(true);
    setMetadataError(null);
    try {
      const result = await api.refreshMetadata(itemId);
      setMetadata(result.metadata);
      setMetadataEnabled(true);
    } catch (caught) {
      setMetadataError(caught instanceof Error ? caught.message : 'Metadata güncellenemedi.');
    } finally {
      setMetadataBusy(false);
    }
  };

  const matchMetadata = async (externalId: string): Promise<void> => {
    if (metadataBusy) return;
    setMetadataBusy(true);
    setMetadataError(null);
    try {
      const result = await api.matchMetadata(itemId, externalId);
      setMetadata(result.metadata);
      setMetadataEnabled(true);
      setShowMatch(false);
    } catch (caught) {
      setMetadataError(caught instanceof Error ? caught.message : 'Eşleştirme yapılamadı.');
    } finally {
      setMetadataBusy(false);
    }
  };

  const clearMetadata = async (): Promise<void> => {
    if (metadataBusy) return;
    setMetadataBusy(true);
    setMetadataError(null);
    try {
      await api.clearMetadata(itemId);
      setMetadata(null);
    } catch (caught) {
      setMetadataError(caught instanceof Error ? caught.message : 'Metadata kaldırılamadı.');
    } finally {
      setMetadataBusy(false);
    }
  };

  /** Ayrıntıyı yeniden okur; başlık/yıl ve afiş değişiklikleri anında yansır. */
  const reloadDetail = async (): Promise<void> => {
    try {
      const found = await api.item(itemId);
      setDetail(found);
      if (user !== null) void saveOfflineItem(user.id, found);
    } catch {
      // Kaydetme başarılıysa ekranı eski veriyle bırakmak yeterli.
    }
  };

  const saveMetadata = async (patch: MetadataOverrideInput): Promise<void> => {
    if (metadataBusy) return;
    setMetadataBusy(true);
    setMetadataError(null);
    try {
      const result = await api.updateMetadata(itemId, patch);
      setMetadata(result.metadata);
      setMetadataEnabled(true);
      await reloadDetail();
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Künye kaydedilemedi.';
      setMetadataError(message);
      throw caught;
    } finally {
      setMetadataBusy(false);
    }
  };

  const uploadArtwork = async (kind: 'poster' | 'backdrop', file: File): Promise<void> => {
    const data = await fileToBase64(file);
    await api.uploadArtwork(itemId, { kind, data });
    await reloadDetail();
  };

  const removeArtwork = async (kind: 'poster' | 'backdrop'): Promise<void> => {
    await api.deleteArtwork(itemId, kind);
    await reloadDetail();
  };

  /** "Listem"e ekle/çıkar; iyimser değil, sunucunun yanıtını yansıtır. */
  const toggleWatchlist = async (): Promise<void> => {
    if (detail === null || watchlistBusy) return;
    setWatchlistBusy(true);
    try {
      const result = await api.setWatchlist(detail.id, !inWatchlist);
      setInWatchlist(result.inWatchlist);
    } catch {
      // Sessiz: düğme eski durumda kalır, kullanıcı tekrar deneyebilir.
    } finally {
      setWatchlistBusy(false);
    }
  };

  const toggleWatched = async (): Promise<void> => {
    if (detail === null || watchBusy) return;
    setWatchBusy(true);
    try {
      const result = await api.setWatched(itemId, !detail.isWatched);
      setDetail((current) =>
        current === null
          ? current
          : {
              ...current,
              isWatched: result.isWatched,
              progress: null,
              resumePositionMs: 0,
            },
      );
    } catch {
      // Durum değişmedi; kullanıcı tekrar deneyebilir.
    } finally {
      setWatchBusy(false);
    }
  };

  const resetProgress = async (): Promise<void> => {
    if (watchBusy) return;
    setWatchBusy(true);
    try {
      await api.clearProgress(itemId);
      setDetail((current) =>
        current === null
          ? current
          : { ...current, isWatched: false, progress: null, resumePositionMs: 0 },
      );
    } catch {
      // Durum değişmedi; kullanıcı tekrar deneyebilir.
    } finally {
      setWatchBusy(false);
    }
  };

  if (error) return <ErrorNotice message={error} />;
  if (!detail) return <Loading label="İçerik detayları yükleniyor…" />;

  const episodes = detail.episodes ?? [];
  const children = detail.children ?? [];
  const isPlayable = detail.kind === 'movie' || detail.kind === 'episode';
  const library = libraries?.find((entry) => entry.id === detail.libraryId);
  const copy = mediaCopy(mediaContextFor(detail, library?.kind));

  return (
    <div className="item-detail">
      {/* Sinematik Arka Plan (Backdrop) */}
      <div
        className="item-detail__backdrop"
        style={{
          backgroundImage: `url(${
            resizeProxyImage(detail.backdropUrl ?? metadata?.backdropUrl, 'w780') ??
            detail.thumbnailUrl ??
            mediaUrl(`/api/items/${detail.id}/thumbnail`)
          })`,
        }}
        aria-hidden="true"
      />

      <ItemHero
        detail={detail}
        onWatch={onWatch}
        onBack={onBack}
        inWatchlist={inWatchlist}
        onToggleWatchlist={() => void toggleWatchlist()}
        watchlistBusy={watchlistBusy}
        posterUrl={detail.posterUrl ?? metadata?.posterUrl ?? null}
        metadata={metadata}
        copy={copy}
        continueItem={nextTarget}
        onStartOver={isPlayable ? () => onWatch(detail.id, true) : undefined}
        onDelete={canDelete ? () => requestDelete(detail) : undefined}
      />

      <MetadataPanel
        metadata={metadata}
        enabled={metadataEnabled}
        isAdmin={isAdmin}
        canMatch={detail.kind === 'movie' || detail.kind === 'show'}
        busy={metadataBusy}
        error={metadataError}
        posterUrl={detail.posterUrl ?? metadata?.posterUrl ?? null}
        backdropUrl={detail.backdropUrl ?? metadata?.backdropUrl ?? null}
        artwork={detail.artwork ?? null}
        onRefresh={() => void refreshMetadata()}
        onMatch={() => setShowMatch(true)}
        onClear={() => void clearMetadata()}
        onSave={saveMetadata}
        onUploadArtwork={uploadArtwork}
        onRemoveArtwork={removeArtwork}
      />

      <TechnicalDetails detail={detail} />

      {isPlayable && (
        <section className="item-detail__watch-state" aria-label="İzleme durumu">
          <span className="item-detail__watch-label">
            {detail.isWatched ? 'Bu içerik izlendi.' : 'İzleme durumunu yönet'}
          </span>
          <button
            type="button"
            className="button button--ghost"
            disabled={watchBusy}
            onClick={() => void toggleWatched()}
          >
            {detail.isWatched ? 'İzlemeyi geri al' : 'İzlendi olarak işaretle'}
          </button>
          {detail.progress !== null && (
            <button
              type="button"
              className="button button--ghost"
              disabled={watchBusy}
              onClick={() => void resetProgress()}
            >
              İlerlemeyi sıfırla
            </button>
          )}
        </section>
      )}

      {collection !== null && collection.items.length > 0 && (
        <section className="section item-detail__section">
          <div className="section__header">
            <h2 className="section__title">
              {collection.collection?.name ?? 'Koleksiyon'} ({collection.items.length})
            </h2>
          </div>
          <div className="grid">
            {collection.items.map((member) => (
              <ItemCard key={member.id} item={member} href={`#/oge/${member.id}`} />
            ))}
          </div>
        </section>
      )}

      {episodes.length > 0 && <ItemEpisodes episodes={episodes} onWatch={onWatch} />}

      {children.length > 0 && (
        <ItemChildren items={children} isChildOfEpisodes={episodes.length > 0} />
      )}

      {episodes.length === 0 && children.length === 0 && !isPlayable && (
        <Empty message="Bu öğenin alt içeriği bulunamadı." />
      )}

      {showMatch && (
        <MetadataMatchDialog
          itemTitle={detail.title}
          kind={detail.kind === 'show' ? 'show' : 'movie'}
          busy={metadataBusy}
          onSelect={(externalId) => void matchMetadata(externalId)}
          onClose={() => setShowMatch(false)}
        />
      )}
    </div>
  );
}

/** Gerçek ffprobe verisinden teknik rozetler; veri yoksa hiçbiri gösterilmez. */
function TechnicalBadges({ technical }: { technical: Item['technical'] }): ReactNode {
  if (technical === null || technical === undefined) return null;
  return (
    <>
      {technical.width !== null && technical.height !== null && (
        <span className="tech-badge">
          {technical.width}×{technical.height}
        </span>
      )}
      {technical.videoCodec !== null && (
        <span className="tech-badge">{technical.videoCodec.toUpperCase()}</span>
      )}
      {technical.audioCodec !== null && (
        <span className="tech-badge">{technical.audioCodec.toUpperCase()}</span>
      )}
      {technical.container !== null && (
        <span className="tech-badge">{technical.container.toUpperCase()}</span>
      )}
    </>
  );
}

/** Dizi/sezon birincil eyleminin metni. */
function continueLabel(detail: ItemDetail, target: Item, copy: MediaCopy): string {
  if (target.progress) return copy.resume;
  return detail.kind === 'show' ? `İlk Bölümü ${copy.play}` : `Sıradaki Bölümü ${copy.play}`;
}

function ItemHero({
  detail,
  onWatch,
  onBack,
  inWatchlist,
  onToggleWatchlist,
  watchlistBusy,
  posterUrl,
  metadata,
  copy,
  continueItem,
  onStartOver,
  onDelete,
}: {
  detail: ItemDetail;
  onWatch: (itemId: string) => void;
  onBack?: (() => void) | undefined;
  inWatchlist: boolean;
  onToggleWatchlist: () => void;
  watchlistBusy: boolean;
  /** Metadata posteri (sunucu proxy'si); yoksa yerel thumbnail. */
  posterUrl: string | null;
  /** Karar alanındaki puan, tür ve özet. */
  metadata: ItemMetadata | null;
  /** Film/dizi veya müzik dili. */
  copy: MediaCopy;
  /** Dizi/sezon için ilk izlenmemiş bölüm. */
  continueItem: Item | null;
  /** Kayıtlı konumu yok sayıp baştan oynatır. */
  onStartOver?: (() => void) | undefined;
  /** Yönetici için kalıcı silme. */
  onDelete?: (() => void) | undefined;
}): ReactNode {
  const isPlayable = detail.kind === 'movie' || detail.kind === 'episode';
  const resumeMs = detail.resumePositionMs ?? detail.progress?.positionMs;
  const ratio = progressRatio(resumeMs, detail.durationMs);
  const percent = Math.round(ratio * 100);

  return (
    <div className="item-detail__hero">
      <ItemPoster detail={detail} posterUrl={posterUrl} />

      <div className="item-detail__info">
        <div className="item-detail__badges">
          <span className="item-detail__badge-kind">{KIND_LABEL[detail.kind] ?? detail.kind}</span>
          {detail.year !== null && <span className="item-detail__badge">{detail.year}</span>}
          {detail.durationMs !== null && (
            <span className="item-detail__badge">{formatDuration(detail.durationMs)}</span>
          )}
          {detail.isWatched && <span className="badge badge--watched">{copy.watched}</span>}
        </div>

        <h1 className="item-detail__title">{displayTitle(detail)}</h1>

        <MetadataIntro metadata={metadata} />

        {ratio > 0 && ratio < 0.95 && (
          <div className="item-detail__progress-box">
            <div className="item-detail__progress-text">
              <span>{resumeMs ? formatDuration(resumeMs) : '0:00'}</span>
              <span>
                %{percent} {copy.progress}
              </span>
              <span>{detail.durationMs ? formatDuration(detail.durationMs) : ''}</span>
            </div>
            <div className="progress" aria-hidden="true">
              <div className="progress__fill" style={{ width: `${percent}%` }} />
            </div>
          </div>
        )}

        <div className="item-detail__actions">
          {isPlayable && (
            <button
              type="button"
              className="button button--primary item-detail__play-btn"
              onClick={() => onWatch(detail.id)}
            >
              <span aria-hidden="true">
                <PlayIcon size={16} />
              </span>
              {percent > 0 && percent < 95 ? copy.resume : copy.play}
            </button>
          )}

          {isPlayable && percent > 0 && onStartOver !== undefined && (
            <button
              type="button"
              className="button button--ghost item-detail__restart-btn"
              onClick={onStartOver}
            >
              {copy.restart}
            </button>
          )}

          {!isPlayable && continueItem !== null && (
            <button
              type="button"
              className="button button--primary item-detail__play-btn"
              onClick={() => onWatch(continueItem.id)}
            >
              <span aria-hidden="true">
                <PlayIcon size={16} />
              </span>
              {continueLabel(detail, continueItem, copy)}
            </button>
          )}

          {isPlayable && (
            <button
              type="button"
              className="button button--ghost item-detail__watchlist-btn"
              onClick={onToggleWatchlist}
              disabled={watchlistBusy}
              aria-pressed={inWatchlist}
              title={inWatchlist ? copy.removeWatchlist : copy.addWatchlist}
            >
              {inWatchlist ? <BookmarkFilledIcon size={16} /> : <BookmarkIcon size={16} />}
              {inWatchlist ? copy.inWatchlist : copy.addWatchlist}
            </button>
          )}

          {onDelete && (
            <button
              type="button"
              className="button button--ghost item-detail__delete-btn"
              onClick={onDelete}
              title="Diskten kalıcı olarak sil"
            >
              <TrashIcon size={16} />
              Sil
            </button>
          )}

          <button
            type="button"
            className="button button--ghost"
            onClick={() => {
              if (onBack) onBack();
              else window.history.back();
            }}
          >
            Geri
          </button>
        </div>
      </div>
    </div>
  );
}

/** Karar alanı: puan, tür ve kısa özet; teknik künye aşağıda ayrı bölümde. */
function MetadataIntro({ metadata }: { metadata: ItemMetadata | null }): ReactNode {
  const [expanded, setExpanded] = useState(false);
  if (metadata === null) return null;

  const rating = preferredRating(metadata);
  const overviewText = metadata.overview;
  const overview = overviewText === null ? null : overviewPreview(overviewText);

  return (
    <div className="item-detail__intro">
      {metadata.tagline !== null && <p className="item-detail__tagline">“{metadata.tagline}”</p>}

      {(rating !== null || metadata.genres.length > 0) && (
        <div className="item-detail__meta-row">
          {rating !== null && (
            <span
              className="item-detail__rating"
              title={
                rating.votes === null
                  ? `${rating.source} puanı`
                  : `${rating.votes.toLocaleString('tr-TR')} ${rating.source} oyu`
              }
            >
              {rating.source} {rating.value.toFixed(1)}
            </span>
          )}
          {metadata.genres.map((genre) => (
            <span className="item-detail__genre" key={genre}>
              {genre}
            </span>
          ))}
        </div>
      )}

      {overview !== null && (
        <div className="item-detail__overview">
          <p>{expanded ? overviewText : overview.text}</p>
          {overview.truncated && (
            <button
              type="button"
              className="item-detail__overview-toggle"
              aria-expanded={expanded}
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded ? 'Daralt' : 'Özeti genişlet'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Teknik künye ana karar alanını işgal etmez; isteğe bağlı açılır. */
function TechnicalDetails({ detail }: { detail: ItemDetail }): ReactNode {
  const subLangs = Array.from(
    new Set(
      detail.subtitles
        ?.map((s) => s.language?.toUpperCase() || s.title || s.codec.toUpperCase())
        .filter(Boolean),
    ),
  );
  if (subLangs.length === 0 && (detail.technical === null || detail.technical === undefined)) {
    return null;
  }

  return (
    <details className="item-detail__technical">
      <summary>Teknik ayrıntılar</summary>
      <div className="item-detail__tech-specs">
        {subLangs.length > 0 && (
          <span className="tech-badge" title="Mevcut Altyazılar">
            <CaptionsIcon size={13} />
            {subLangs.join(', ')}
          </span>
        )}
        <TechnicalBadges technical={detail.technical} />
      </div>
    </details>
  );
}

function ItemPoster({
  detail,
  posterUrl,
}: {
  detail: ItemDetail;
  posterUrl: string | null;
}): ReactNode {
  return (
    <div className={`item-detail__poster item-detail__poster--${detail.kind}`}>
      <img
        src={
          resizeProxyImage(posterUrl, 'w500') ??
          detail.thumbnailUrl ??
          mediaUrl(`/api/items/${detail.id}/thumbnail`)
        }
        alt={detail.title}
        className="item-detail__poster-img"
        onError={(event) => {
          const image = event.currentTarget;
          const local = mediaUrl(`/api/items/${detail.id}/thumbnail`);
          if (posterUrl !== null && !image.src.endsWith(local)) {
            image.src = local;
            return;
          }
          image.style.display = 'none';
        }}
      />
      <span className="item-detail__poster-fallback" aria-hidden="true">
        {detail.kind === 'movie' ? <FilmIcon size={40} /> : <TvIcon size={40} />}
      </span>
    </div>
  );
}

function ItemEpisodes({
  episodes,
  onWatch,
}: {
  episodes: Item[];
  onWatch: (itemId: string) => void;
}): ReactNode {
  return (
    <section className="section item-detail__section">
      <h2 className="section__title">Bölümler ({episodes.length})</h2>
      <div className="grid">
        {episodes.map((episode) => (
          <ItemCard
            key={episode.id}
            item={episode}
            layout="landscape"
            href={`#/izle/${episode.id}`}
            onClick={() => onWatch(episode.id)}
          />
        ))}
      </div>
    </section>
  );
}

function ItemChildren({
  items,
  isChildOfEpisodes,
}: {
  items: Item[];
  isChildOfEpisodes: boolean;
}): ReactNode {
  return (
    <section className="section item-detail__section">
      <h2 className="section__title">
        {isChildOfEpisodes ? 'İlgili' : 'Sezonlar'} ({items.length})
      </h2>
      <div className="grid">
        {items.map((child) => (
          <ItemCard key={child.id} item={child} href={`#/oge/${child.id}`} />
        ))}
      </div>
    </section>
  );
}
